import { Reference } from '@approvalmax/types';
import { CheckboxEditor, DateTimeEditor, DropdownEditor, TextEditor } from '@approvalmax/ui';
import { Flex } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { usePrevious } from 'react-use';
import { useNetSuiteBillPaymentFieldSettings, useResetSplitView } from 'shared/hooks';
import { ContentSplitViewMode, NetSuiteBillPaymentFields, netSuiteBillPaymentFieldValuesState } from 'shared/states';

import { SectionContainer } from '../../../../../components/SectionContainer';
import { NetSuiteContext } from '../../../../../data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from '../../../../../selectors/netsuite/netSuiteSelectors';
import {
    NetSuiteAccountsDataProvider,
    NetSuiteCurrencySection,
    NetSuiteCustomFields,
    NetSuitePayeesDataProvider,
} from '../../../components';
import { usePayeeData } from '../../../hooks';
import {
    isFieldEditable,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
    isOptionalFieldInvalid,
} from '../../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteShowErrorsState } from '../../../resources/states';
import {
    useNetSuiteBillPaymentClearItems,
    useNetSuiteBillPaymentFields,
} from '../../NetSuiteBillPaymentRequestForm.hooks';
import { netSuiteBillPaymentShowPopupErrorsState } from '../../NetSuiteBillPaymentRequestForm.states';
import { messages } from './NetSuiteBillPaymentFieldsSection.messages';
import { MemoEditor, StyledField } from './NetSuiteBillPaymentFieldsSection.styles';
import { NetSuiteBillPaymentFieldsSectionProps } from './NetSuiteBillPaymentFieldsSection.types';

const { netSuiteConstants } = constants;

export const NetSuiteBillPaymentFieldsSection: FC<NetSuiteBillPaymentFieldsSectionProps> = (props) => {
    const { requestId, companyId, workflowId, savedPayeeId } = props;

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const fieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);
    const showPopupErrors = useAtomValue(netSuiteBillPaymentShowPopupErrorsState);

    const { currencies, documentFields } = netSuiteEditingContext;

    const { onFieldChange, updateFieldsBulk } = useNetSuiteBillPaymentFields();

    const clearItems = useNetSuiteBillPaymentClearItems();

    const resetSplitView = useResetSplitView({
        views: [
            ContentSplitViewMode.AttachmentsToNetSuiteBillLinkedToBillPayment,
            ContentSplitViewMode.NetSuiteBillLinkedToBillPayment,
        ],
    });

    const fieldSettings = useNetSuiteBillPaymentFieldSettings(documentFields);

    const { payeeData, isLoading: isPayeeDataLoading } = usePayeeData({
        requestId,
        companyId,
        currencies,
        payeeId: fieldValues.payee?.id,
        payeeType: fieldValues.payee?.type,
    });

    const prevPayeeData = usePrevious(payeeData);

    useEffect(() => {
        if (!prevPayeeData && payeeData && payeeData.id === savedPayeeId) {
            return;
        }

        if (!payeeData || prevPayeeData?.id === payeeData?.id) {
            return;
        }

        if (
            payeeData.currency !== fieldValues.currency ||
            payeeData.defaultPayablesAccount !== fieldValues.payablesAccount
        ) {
            clearItems();
        }

        updateFieldsBulk({
            payeeAddress: payeeData.defaultBillingAddress,
            currency: payeeData.currency,
            payablesAccount: payeeData.defaultPayablesAccount,
        });
    }, [payeeData, prevPayeeData, savedPayeeId, fieldValues, clearItems, updateFieldsBulk]);

    const onEditorChange = useCallback(
        <K extends keyof NetSuiteBillPaymentFields>(key: K) =>
            (value: NetSuiteBillPaymentFields[K]) =>
                onFieldChange(key, value),
        [onFieldChange]
    );

    const onPayeeChange = useCallback(
        (payee: domain.NetSuitePayee | null) => {
            resetSplitView();

            if (!payee) {
                updateFieldsBulk({
                    payee: null,
                    currency: null,
                    payeeAddress: null,
                });

                return;
            }

            const { defaultCurrencyId, defaultBillingAddress } = payee;
            const currency = currencies.find(({ id }) => id === defaultCurrencyId);

            if (currency?.id !== fieldValues.currency?.id) {
                clearItems();
            }

            updateFieldsBulk({
                payee,
                currency,
                payeeAddress: defaultBillingAddress,
            });
        },
        [currencies, fieldValues, clearItems, updateFieldsBulk, resetSplitView]
    );

    const onPayablesAccountChange = useCallback(
        (payablesAccount: Reference | null) => {
            resetSplitView();

            if (payablesAccount?.id !== fieldValues.payablesAccount?.id) {
                clearItems();
            }

            onFieldChange('payablesAccount', payablesAccount);
        },
        [fieldValues.payablesAccount, onFieldChange, clearItems, resetSplitView]
    );

    const onCurrencyChange = useCallback(
        (currency: NetSuiteContext['currencies'][0]) => {
            resetSplitView();

            if (currency?.id !== fieldValues.currency?.id) {
                clearItems();
            }

            updateFieldsBulk({
                currency,
                exchangeRate: null,
            });
        },
        [fieldValues.currency, updateFieldsBulk, clearItems, resetSplitView]
    );

    const payeeCurrencies = useMemo(() => {
        return currencies.filter((currency) => payeeData?.currencyIds?.includes(currency.id));
    }, [payeeData, currencies]);

    return (
        <>
            <SectionContainer title={messages.vendorDetailsSectionName}>
                <Flex container alignItems='start' wrap>
                    <StyledField title={messages.payeeFieldText} required>
                        <NetSuitePayeesDataProvider>
                            <DropdownEditor
                                value={fieldValues.payee || null}
                                singleLinePerItem={false}
                                onChange={onPayeeChange}
                                placeholder={messages.payeeFieldPlaceholder}
                                invalid={(showErrors || showPopupErrors) && !fieldValues.payee}
                            />
                        </NetSuitePayeesDataProvider>
                    </StyledField>

                    {isFieldVisible(fieldSettings.payeeAddress) && (
                        <StyledField
                            title={messages.payeeAddressFieldText}
                            required={isFieldMandatory(fieldSettings.payeeAddress)}
                        >
                            <PlainDataProvider items={payeeData?.address ?? []}>
                                <DropdownEditor
                                    value={fieldValues.payeeAddress || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('payeeAddress')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.payeeAddress)
                                            ? messages.payeeAddressPlaceholder
                                            : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.payeeAddress,
                                        fieldSettings.payeeAddress
                                    )}
                                    loading={isPayeeDataLoading}
                                    displayAttribute='fullAddress'
                                    disabled={isFieldReadOnly(fieldSettings.payeeAddress)}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}
                </Flex>
            </SectionContainer>

            <NetSuiteCurrencySection
                currencies={payeeCurrencies}
                fieldValues={fieldValues}
                fieldSettings={fieldSettings}
                isLoadingCurrencies={isPayeeDataLoading}
                onExchangeRateChange={onEditorChange('exchangeRate')}
                onCurrencyChange={onCurrencyChange}
            />

            <SectionContainer title={messages.billPaymentDetailsSectionName}>
                <Flex container alignItems='start' wrap>
                    {isFieldVisible(fieldSettings.transactionNumber) && (
                        <StyledField title={messages.transactionNumberFieldText}>
                            <TextEditor
                                value={fieldValues.transactionNumber || ''}
                                onChange={onEditorChange('transactionNumber')}
                                disabled
                            />
                        </StyledField>
                    )}

                    <StyledField title={messages.checkNumberFieldText}>
                        <TextEditor
                            value={fieldValues.checkNumber || ''}
                            onChange={onEditorChange('checkNumber')}
                            placeholder={messages.textFieldPlaceholder}
                            changeOnBlur
                            maxLength={netSuiteConstants.referenceMaxLength}
                        />
                    </StyledField>

                    <StyledField title={messages.transactionDateFieldText} required>
                        <DateTimeEditor
                            value={fieldValues.transactionDate || ''}
                            placeholder={messages.dateFieldPlaceholder}
                            invalid={showErrors && !fieldValues.transactionDate}
                            onChange={onEditorChange('transactionDate')}
                        />
                    </StyledField>

                    <StyledField title={messages.payablesAccountFieldText} required>
                        <NetSuiteAccountsDataProvider>
                            <DropdownEditor
                                value={fieldValues.payablesAccount || null}
                                singleLinePerItem={false}
                                onChange={onPayablesAccountChange}
                                placeholder={messages.accountFieldPlaceholder}
                                invalid={(showErrors || showPopupErrors) && !fieldValues.payablesAccount}
                                loading={false}
                            />
                        </NetSuiteAccountsDataProvider>
                    </StyledField>

                    <StyledField title={messages.accountFieldText} required>
                        <NetSuiteAccountsDataProvider group={domain.NetSuiteAccountTypeGroup.BillPayments}>
                            <DropdownEditor
                                value={fieldValues.account || null}
                                singleLinePerItem={false}
                                onChange={onEditorChange('account')}
                                placeholder={messages.accountFieldPlaceholder}
                                invalid={showErrors && !fieldValues.account}
                                loading={false}
                            />
                        </NetSuiteAccountsDataProvider>
                    </StyledField>

                    {isFieldVisible(fieldSettings.department) && (
                        <StyledField
                            title={messages.departmentFieldText}
                            required={isFieldMandatory(fieldSettings.department)}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.departments}>
                                <DropdownEditor
                                    value={fieldValues.department || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('department')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.department)
                                            ? messages.departmentFieldPlaceholder
                                            : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.department,
                                        fieldSettings.department
                                    )}
                                    disabled={isFieldReadOnly(fieldSettings.department)}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {isFieldVisible(fieldSettings.class) && (
                        <StyledField title={messages.classFieldText} required={isFieldMandatory(fieldSettings.class)}>
                            <PlainDataProvider items={netSuiteEditingContext.classes}>
                                <DropdownEditor
                                    value={fieldValues.class || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('class')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.class) ? messages.classFieldPlaceholder : ''
                                    }
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.class, fieldSettings.class)}
                                    disabled={isFieldReadOnly(fieldSettings.class)}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    {isFieldVisible(fieldSettings.location) && (
                        <StyledField
                            title={messages.locationFieldText}
                            required={isFieldMandatory(fieldSettings.location)}
                        >
                            <PlainDataProvider items={netSuiteEditingContext.locations}>
                                <DropdownEditor
                                    value={fieldValues.location || null}
                                    singleLinePerItem={false}
                                    onChange={onEditorChange('location')}
                                    placeholder={
                                        isFieldEditable(fieldSettings.location) ? messages.locationFieldPlaceholder : ''
                                    }
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.location,
                                        fieldSettings.location
                                    )}
                                    disabled={isFieldReadOnly(fieldSettings.location)}
                                    loading={false}
                                />
                            </PlainDataProvider>
                        </StyledField>
                    )}

                    <StyledField title={messages.postingPeriodFieldText}>
                        <PlainDataProvider items={[]}>
                            <DropdownEditor
                                value={fieldValues.postingPeriod || null}
                                onChange={onEditorChange('postingPeriod')}
                                placeholder=''
                                disabled
                            />
                        </PlainDataProvider>
                    </StyledField>

                    {isFieldVisible(fieldSettings.toBePrinted) && (
                        <StyledField title={messages.toBePrintedFieldText}>
                            <CheckboxEditor
                                onChange={onEditorChange('toBePrinted')}
                                value={fieldValues.toBePrinted || false}
                                disabled={isFieldReadOnly(fieldSettings.toBePrinted)}
                            >
                                {messages.toBePrintedCheckboxLabel}
                            </CheckboxEditor>
                        </StyledField>
                    )}

                    {isFieldVisible(fieldSettings.printVoucher) && (
                        <StyledField title={messages.printVoucherFieldText}>
                            <CheckboxEditor
                                onChange={onEditorChange('printVoucher')}
                                value={fieldValues.printVoucher || false}
                                disabled={isFieldReadOnly(fieldSettings.printVoucher)}
                            >
                                {messages.printVoucherCheckboxLabel}
                            </CheckboxEditor>
                        </StyledField>
                    )}

                    <NetSuiteCustomFields workflowId={workflowId} showErrors={showErrors} />
                </Flex>
            </SectionContainer>

            {isFieldVisible(fieldSettings.memo) && (
                <SectionContainer title={messages.memoSectionName}>
                    <MemoEditor
                        value={fieldValues.memo || ''}
                        onChange={onEditorChange('memo')}
                        placeholder={isFieldEditable(fieldSettings.memo) ? messages.textFieldPlaceholder : ''}
                        changeOnBlur
                        minHeight={5}
                        maxLength={netSuiteConstants.memoMaxLength}
                        invalid={isOptionalFieldInvalid(showErrors, fieldValues.memo, fieldSettings.memo)}
                        disabled={isFieldReadOnly(fieldSettings.memo)}
                    />
                </SectionContainer>
            )}
        </>
    );
};

NetSuiteBillPaymentFieldsSection.displayName = 'NetSuiteBillPaymentFieldsSection';
